import { FC } from "react";
import moment from "moment";
import { useReactiveVar } from "@apollo/client";
import { currentConfigVar } from "apollo/cache/config";
import { Avatar } from "components/core";
import { classNames } from "utils";
import lodash from "lodash";

interface InstallationViewProps {
  installation: {
    history: any;
    installationSync: any;
    installationMeterSystemSyncMethod: string;
    reverseSyncedAt: any;
    resolution: any;
    assigneeType: string;
    contractorAssigner: any;
    installationMeterSystemSyncStatus: string;
    meterContractor: any;
    farmingOutContractor: any;
    farmingOutContractorUser: any;
    servicePointCode: string;
    installationType: {
      code: string;
      name: string;
    };
    installationMeter: {
      _id: any;
      modelMeta: {
        brandCode: string;
        modelCode: string;
        brandName: string;
        modelName: string;
        phase: string;
      };
      code: string;
    };
    request: {
      inspection: any;
      type: string;
      response: {
        servicePoint: {
          code: string;
        };
      };
      resolution: {
        service: {
          meterPhase: string;
        };
      };
    };

    category: string | undefined;
    code: string;
    status: string;
    type: string;
    priority: string;
    region: {
      code: string;
      name: string;
    };
    district: {
      code: string;
      name: string;
    };
    contactPerson: {
      fullName: string;
      phoneNumber: string;
    };
    location: {
      address: string;
      community: string;
    };
    assigner: {
      _id: string;
      code: string;
      lastName: string;
      firstName: string;
      profileImageUrl: string;
      phoneNumber: string;
      emailAddress: string;
    };
    assignee: {
      _id: string;
      code: string;
      lastName: string;
      firstName: string;
      profileImageUrl: string;
      phoneNumber: string;
      emailAddress: string;
    };
    estimatedResolutionDate: string;
    createdAt: string;
    updatedAt: string;
  };
}

function Owner({
  owner,
  type,
}: {
  owner: any;
  type: "field-agents" | "staff-accounts";
}) {
  return (
    <div
      className={classNames(
        "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-500",
        "focus:outline-none relative rounded-lg border p-2 shadow-sm flex items-center space-x-3 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
      )}
    >
      <div className="flex-shrink-0">
        <Avatar
          alt={
            [
              (owner?.lastName || "")?.trim(),
              (owner?.firstName || owner?.name || "")?.trim(),
            ]
              .join(" ")
              .trim() || "N A"
          }
          src={owner?.profileImageUrl || ""}
        />
      </div>
      <div className="flex-1 min-w-0 text-left">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {[
            (owner?.lastName || "")?.trim(),
            (owner?.firstName || owner?.name || "")?.trim(),
          ]
            .join(" ")
            .trim() || "N A"}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
          {owner?.phoneNumber || owner?.code || "N/A"}
        </p>
      </div>
    </div>
  );
}

export const InstallationView: FC<InstallationViewProps> = ({ installation }) => {
  const { dateFormat } = useReactiveVar(currentConfigVar);

  return (
    <div className="space-y-6 divide-y divide-gray-200 flex-1 flex flex-col">
    {installation ? (
      <>
        <div className="">
          <div className="flex justify-between items-center cursor-pointer">
            <div>
              <h3 className="text-md leading-4 font-normal text-gray-900 dark:text-gray-100">
                Order Information
              </h3>
              <p className="mt-1 text-xs text-gray-500">
                Details about service order
              </p>
            </div>
            <div />
          </div>
          <div className="mt-4 space-y-3">
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md p-3 space-y-6 divide-y divide-gray-200">
              <div>
                <span className="text-xs font-light">Order Information</span>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  <div>
                    <span className="block text-sm font-light text-gray-700">
                      Code
                    </span>
                    <div className="mt-1 block w-full sm:text-sm">
                      {installation?.code || "N/A"}
                    </div>
                  </div>
                  <div>
                    <span className="block text-sm font-light text-gray-700">
                      Region
                    </span>
                    <div className="mt-1 block w-full sm:text-sm">
                      {installation?.region?.name || "N/A"}
                    </div>
                  </div>
                  <div>
                    <span className="block text-sm font-light text-gray-700">
                      District
                    </span>
                    <div className="mt-1 block w-full sm:text-sm">
                      {installation?.district?.name || "N/A"}
                    </div>
                  </div>
                  <div>
                    <span className="block text-sm font-light text-gray-700">
                      Date
                    </span>
                    <div className="mt-1 block w-full sm:text-sm">
                      {installation?.createdAt
                        ? moment(installation.createdAt).format(dateFormat)
                        : "N/A"}
                    </div>
                  </div>
                  <div>
                    <span className="block text-sm font-light text-gray-700">
                      Status
                    </span>
                    <div className="mt-1 block w-full sm:text-sm">
                      {lodash.startCase(installation?.status) || "N/A"}
                    </div>
                  </div>
                  {[
                    "AssignedToInstaller",
                    "InProgress",
                    "Resolved",
                    "Completed",
                  ].includes(installation?.status) && (
                    <div className="">
                      <span className="block text-sm font-light text-gray-700">
                        Prepayment Sync Status
                      </span>
                      <div className="mt-1 block w-full sm:text-sm">
                        {installation?.installationMeterSystemSyncStatus || "N/A"}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md p-3 space-y-6 divide-y divide-gray-200">
              <div>
                <span className="text-xs font-light">Customer Information</span>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  <div>
                    <span className="block text-sm font-light text-gray-700">
                      Service Point Number
                    </span>
                    <div className="mt-1 block w-full sm:text-sm">
                      {installation?.servicePointCode || "N/A"}
                    </div>
                  </div>
                  <div>
                    <span className="block text-sm font-light text-gray-700">
                      Full Name
                    </span>
                    <div className="mt-1 block w-full sm:text-sm">
                      {installation?.contactPerson?.fullName || "N/A"}
                    </div>
                  </div>

                  <div>
                    <span className="block text-sm font-light text-gray-700">
                      Phone Number
                    </span>
                    <div className="mt-1 block w-full sm:text-sm">
                      {installation?.contactPerson?.phoneNumber || "N/A"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {installation?.status !== "Pending" && (
          <div className="pt-6">
            <div className="flex justify-between items-center cursor-pointer">
              <div>
                <h3 className="text-md leading-4 font-normal text-gray-900 dark:text-gray-100">
                  Assignment Information
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  Details about meter and installer assigned to service order
                </p>
              </div>
              <div />
            </div>
            <div className="mt-4 space-y-3">
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md p-3 space-y-6 divide-y divide-gray-200">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <span className="block text-sm font-light text-gray-700">
                      Meter Number
                    </span>
                    <div className="mt-1 block w-full sm:text-sm">
                      {installation?.installationMeter?.code || "N/A"}
                    </div>
                  </div>
                  <div>
                    <span className="block text-sm font-light text-gray-700">
                      Meter Make
                    </span>
                    <div className="mt-1 block w-full sm:text-sm">
                      {installation?.installationMeter?.modelMeta?.brandCode ||
                        "N/A"}{" "}
                      -{" "}
                      {installation?.installationMeter?.modelMeta?.brandName ||
                        "N/A"}
                    </div>
                  </div>
                  <div>
                    <span className="block text-sm font-light text-gray-700">
                      Meter Model
                    </span>
                    <div className="mt-1 block w-full sm:text-sm">
                      {installation?.installationMeter?.modelMeta?.modelCode ||
                        "N/A"}{" "}
                      -{" "}
                      {installation?.installationMeter?.modelMeta?.modelName ||
                        "N/A"}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-6 mt-2">
                {installation?.assigner && (
                  <div>
                    <span className="block text-sm font-light text-gray-700">
                      Assigned By
                    </span>
                    <div className="mt-1 block w-full sm:text-sm">
                      <Owner owner={installation?.assigner} type="staff-accounts" />
                    </div>
                  </div>
                )}
                {installation?.assignee && (
                  <div>
                    <span className="block text-sm font-light text-gray-700">
                      Assignee (
                      {installation?.assigneeType === "MeterManufacturingContractor"
                        ? "Meter Supplier"
                        : lodash.startCase(installation?.assigneeType)}
                      )
                    </span>
                    <div className="mt-1 block w-full sm:text-sm">
                      <Owner
                        owner={
                          installation?.assignee ||
                          installation?.meterContractor ||
                          installation?.farmingOutContractor
                        }
                        type="field-agents"
                      />
                    </div>
                  </div>
                )}
                {installation?.assigneeType === "MeterManufacturingContractor" &&
                  installation.farmingOutContractor && (
                    <>
                      <div>
                        <span className="block text-sm font-light text-gray-700">
                          Farming Out Contractor
                        </span>
                        <div className="mt-1 block w-full sm:text-sm">
                          <Owner
                            owner={installation?.farmingOutContractor}
                            type="field-agents"
                          />
                        </div>
                      </div>
                    </>
                  )}
              </div>
            </div>
          </div>
        )}
        </>
      ) : (
        <div
          className={classNames(
            false
              ? "border-amber-400 bg-amber-50 dark:border-amber-600"
              : "border-gray-300 dark:border-gray-600",
            "border-2 border-dashed flex-1 rounded-md p-16 space-y-6 divide-y divide-gray-200 text-center items-center justify-center flex text-sm"
          )}
        >
          No Service Order Found
        </div>
      )}
    </div>
  );
};

export default InstallationView;
